import Head from 'next/head';
import { isAfter } from 'date-fns';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { AUCTION_COLLECTION_PAGE_REDIRECT } from 'collections/enums/AuctionCollectionPageRedirect';
import { COLLECTION_PAGE_REDIRECTS } from 'collections/enums/CollectionPageRedirects';
import { ConstructorNodeClient } from 'search/utils/ConstructorNodeClient';
import { getConstructorNodeClientUserParams } from 'search/utils/getConstructorNodeClientUserParams';
import { getGlobalPricingHiddenFacets } from 'search/utils/getGlobalPricingHiddenFacets';
import { getSearchStateFromUrl } from 'search/utils/getSearchStateFromUrl';
import { GLOBAL_PRICING_ID } from 'shared/enums/GlobalPricing';
import { RESULTS_PER_PAGE } from 'search/enums/constants';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { useCountryCodeTitle } from 'shared/hooks';

const Collection = ({ constructorResponse }) => {
  const { t } = useTranslation();

  const { response } = constructorResponse;
  const { collection } = response;
  const {
    display_name: displayName,
    id,
    data,
  }: {
    // eslint-disable-next-line camelcase
    display_name: string;
    id: string;
    data: {
      description: string;
      // eslint-disable-next-line camelcase
      is_no_index: boolean;
      title: string;
      metadataDescription: string;
      // eslint-disable-next-line camelcase
      header_asset?: { url: string; type: string };
    };
  } = collection;

  const metaImage = data?.header_asset?.type === 'picture' && data?.header_asset?.url;

  const titleWithCountryCode = useCountryCodeTitle({
    defaultTitle: t('collectionPage.metaTitle', '{name} Collection ', {
      name: data?.title || displayName,
    }),
  });
  return (
    <Collection.Container>
      <Head>
        <title>{titleWithCountryCode}</title>
        <meta
          name="description"
          content={
            data?.metadataDescription ||
            t('collectionPage.metaDescription', 'Shop {name} now on GOAT. Authenticity Assured.', {
              name: data?.title || displayName,
            })
          }
          key="description"
        />
        <meta
          property="og:description"
          content={
            data?.metadataDescription ||
            t('collectionPage.metaDescription', 'Shop {name} now on GOAT. Authenticity Assured.', {
              name: data?.title || displayName,
            })
          }
          key="og:description"
        />
        {metaImage && (
          <>
            <meta name="twitter:image" content={metaImage} key="twitter:image" />
            <meta property="og:image" content={metaImage} key="og:image" />
          </>
        )}
      </Head>
      <SearchGrid
        browseKey="collection_id"
        browseValue={id}
        constructorResponse={constructorResponse}
        description={data?.description}
        noIndex={data?.is_no_index}
        title={data?.title || displayName}
      />
    </Collection.Container>
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req }) => {
  const { slug }: { slug: string } = query;
  const searchStateFromQuery = getSearchStateFromUrl(query, resolvedUrl);
  const globalPricingIdCookie: string = req?.cookies?.[GLOBAL_PRICING_ID];
  const globalPricingId = globalPricingIdCookie && JSON.parse(globalPricingIdCookie);

  try {
    // For better SEO, we are redirecting (301) some duplicate collections
    if (COLLECTION_PAGE_REDIRECTS[slug]) {
      return {
        redirect: {
          statusCode: 301,
          destination: COLLECTION_PAGE_REDIRECTS[slug],
        },
      };
    }

    const constructorUserParams = getConstructorNodeClientUserParams(req);
    // Check if collection for this slug exists, if so return collection
    const constructorResponse = await ConstructorNodeClient.browse.getBrowseResults(
      'collection_id',
      slug,
      {
        resultsPerPage: RESULTS_PER_PAGE,
        ...getGlobalPricingHiddenFacets(globalPricingId),
        ...searchStateFromQuery,
      },
      constructorUserParams,
    );

    const invalidSearch = constructorResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    const auctionEndTime = constructorResponse?.response?.collection?.data?.auctionEndTime;
    const endDate = new Date(
      new Date(auctionEndTime).toLocaleString('en-us', {
        timeZone: 'America/Los_Angeles',
      }),
    );
    const now = new Date(new Date().toLocaleString('en-us', { timeZone: 'America/Los_Angeles' }));

    const isAuctionOver = isAfter(now, endDate);

    if (auctionEndTime && isAuctionOver && AUCTION_COLLECTION_PAGE_REDIRECT[slug]) {
      return {
        redirect: {
          destination: AUCTION_COLLECTION_PAGE_REDIRECT[slug],
          permanent: false,
        },
      };
    }

    return { props: { constructorResponse } };
  } catch {
    return {
      notFound: true,
    };
  }
};

Collection.Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  position: relative;
`;

export default Collection;
